import { zodResolver } from '@hookform/resolvers/zod';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Alert, Box, Button, FormHelperText, Paper, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { unitCreateSchema } from '@app/shared';
import type { UnitCreateInput, UnitDto } from '@app/shared';
import { AgentChainSelect, deepestAgent } from '../../components/AgentChainSelect';
import type { AgentChain } from '../../components/AgentChainSelect';
import { useNotify } from '../../components/NotifyProvider';
import { PageHeader } from '../../components/PageHeader';
import { ErrorState } from '../../components/PageState';
import {
  CheckboxInput,
  SelectInput,
  TextInput,
  YesNoInput,
  applyServerErrors,
} from '../../components/form';
import { useAuth } from '../auth/AuthProvider';
import { useCarriers, usePlanOptions } from '../plans/lookups';
import { useAddUnit, useProductModelOptions } from './api';
import { addFieldError, withRequiredMessages } from './formUtils';
import { productLabel } from './labels';

interface FormValues {
  carrierId: string;
  sim: string;
  imei: string;
  isEsim: boolean;
  ownerUserId: string | null;
  modelId: string;
  zip: string;
  puk2: string;
  poNumber: string;
  reservedForPortin: boolean;
  planId: string | null;
  mdn: string;
}

const EMPTY: FormValues = {
  carrierId: '',
  sim: '',
  imei: '',
  isEsim: false,
  ownerUserId: null,
  modelId: '',
  zip: '',
  puk2: '',
  poNumber: '',
  reservedForPortin: false,
  planId: null,
  mdn: '',
};

const describeAdded = (units: UnitDto[]): string =>
  units.map((u) => (u.kind === 'sim' ? `SIM ${u.sim}` : `IMEI ${u.imei}`)).join(' and ');

/** "Add Single Unit" (PC345): one SIM, one device, or both as a linked pair. */
export function AddUnitPage() {
  const notify = useNotify();
  const { user } = useAuth();
  const isAdmin = user?.level === 'admin';
  const carriers = useCarriers();
  const models = useProductModelOptions();
  const plans = usePlanOptions();
  const addUnit = useAddUnit();
  const [chain, setChain] = useState<AgentChain>({});
  const [added, setAdded] = useState<string | null>(null);

  const resolver = useMemo<Resolver<FormValues>>(() => {
    const base = withRequiredMessages<FormValues>(zodResolver(unitCreateSchema), {
      carrierId: 'Select a carrier',
      modelId: 'Select a product',
    });
    return async (values, context, options) => {
      const result = await base(values, context, options);
      // Only an administrator may leave stock unassigned; the API enforces the same rule.
      if (!isAdmin && !values.ownerUserId)
        return addFieldError(result, 'ownerUserId', 'Select the user this unit is assigned to');
      return result;
    };
  }, [isAdmin]);

  const { control, handleSubmit, reset, getValues, setValue, setError, setFocus, formState } =
    useForm<FormValues>({
      defaultValues: EMPTY,
      resolver,
    });

  const submit = handleSubmit(async (values) => {
    setAdded(null);
    try {
      // The resolver hands over the parsed schema output, which is what the API expects.
      const result = await addUnit.mutateAsync(values as UnitCreateInput);
      const message = `${describeAdded(result.data)} added.`;
      notify.success(message);
      setAdded(message);
      // Keep carrier, user, product, ZIP, PO, port-in flag and plan for the next unit of the
      // same delivery; clear what is specific to one unit.
      reset({ ...getValues(), sim: '', imei: '', isEsim: false, puk2: '', mdn: '' });
      setFocus('sim');
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });

  const lookupError = carriers.error ?? models.error ?? plans.error;

  return (
    <Box>
      <PageHeader
        title="Add Unit"
        code="PC345"
        actions={
          <Button component={Link} to="/inventory" startIcon={<ArrowBackIcon />} variant="outlined">
            Back to Inventory Dashboard
          </Button>
        }
      />
      {lookupError ? (
        <ErrorState
          error={lookupError}
          onRetry={() => {
            void carriers.refetch();
            void models.refetch();
            void plans.refetch();
          }}
        />
      ) : null}

      <Paper
        variant="outlined"
        component="form"
        noValidate
        onSubmit={(e) => void submit(e)}
        sx={{ p: 2, maxWidth: 960 }}
      >
        <Typography variant="h3" sx={{ mb: 2 }}>
          Add Single Unit
        </Typography>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
          <SelectInput
            control={control}
            name="carrierId"
            label="Carrier"
            required
            options={(carriers.data ?? [])
              .filter((c) => c.status === 'Active')
              .map((c) => ({ value: c.id, label: `${c.name} (${c.code})` }))}
          />
          <Box />
          <TextInput
            control={control}
            name="sim"
            label="SIM"
            autoComplete="off"
            slotProps={{ htmlInput: { maxLength: 22, inputMode: 'numeric' } }}
          />
          <TextInput
            control={control}
            name="imei"
            label="IMEI"
            autoComplete="off"
            slotProps={{ htmlInput: { maxLength: 16, inputMode: 'numeric' } }}
          />
          {/* Assumption: the reference form has no eSIM field, but its dashboard counts and filters
              eSIMs, so the flag has to be recorded when a SIM is added. */}
          <Box sx={{ gridColumn: '1 / -1' }}>
            <CheckboxInput control={control} name="isEsim" label="eSIM" />
            {formState.errors.isEsim ? (
              <FormHelperText error>{formState.errors.isEsim.message}</FormHelperText>
            ) : null}
          </Box>

          <Box
            component="fieldset"
            sx={{ gridColumn: '1 / -1', border: 0, p: 0, m: 0, minWidth: 0 }}
          >
            <Typography component="legend" sx={{ mb: 1, fontWeight: 600 }}>
              Assign to user{isAdmin ? '' : ' *'}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, 1fr)' },
              }}
            >
              <AgentChainSelect
                value={chain}
                onChange={(next) => {
                  setChain(next);
                  setValue('ownerUserId', deepestAgent(next) ?? null, {
                    shouldDirty: true,
                    shouldValidate: formState.isSubmitted,
                  });
                }}
              />
            </Box>
            <FormHelperText error={Boolean(formState.errors.ownerUserId)}>
              {formState.errors.ownerUserId?.message ??
                (isAdmin
                  ? 'The unit goes to the last user selected. Leave empty to keep it as unassigned stock.'
                  : 'The unit goes to the last user selected.')}
            </FormHelperText>
          </Box>

          <SelectInput
            control={control}
            name="modelId"
            label="Product Name"
            required
            options={(models.data ?? []).map((m) => ({ value: m.id, label: productLabel(m) }))}
          />
          <TextInput
            control={control}
            name="zip"
            label="Zipcode"
            slotProps={{ htmlInput: { maxLength: 5, inputMode: 'numeric' } }}
          />
          <TextInput
            control={control}
            name="puk2"
            label="PUK 2"
            slotProps={{ htmlInput: { maxLength: 20 } }}
          />
          <TextInput
            control={control}
            name="poNumber"
            label="PO Number"
            slotProps={{ htmlInput: { maxLength: 50 } }}
          />
          <YesNoInput control={control} name="reservedForPortin" label="Reserve for portin" />
          <SelectInput
            control={control}
            name="planId"
            label="Plan"
            emptyLabel="Select"
            options={(plans.data ?? []).map((p) => ({ value: p.id, label: p.name }))}
          />
          <TextInput
            control={control}
            name="mdn"
            label="Telephone Number"
            slotProps={{ htmlInput: { maxLength: 10, inputMode: 'numeric' } }}
          />
        </Box>

        {added ? (
          <Alert severity="success" sx={{ mt: 2 }} onClose={() => setAdded(null)}>
            {added} Enter the next unit.
          </Alert>
        ) : null}
        <Box sx={{ mt: 2 }}>
          <Button type="submit" variant="contained" disabled={addUnit.isPending}>
            Save Unit
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
